import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, MapPinned } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/common/Reveal";
import { CTAButton } from "@/components/common/CTA";
import { waLink } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

export default function Contact() {
  const { contact, company } = useSettings();
  const channels = [
    { icon: Phone, label: "Phone", value: contact.phone, href: contact.phoneHref, cta: "Call our team" },
    { icon: MessageCircle, label: "WhatsApp", value: contact.whatsapp, href: waLink(undefined, contact.whatsappNumber), cta: "Chat on WhatsApp", external: true },
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}`, cta: "Send an email" },
  ];
  return (
    <>
      <SEO
        title="Contact Premier Global Sourcing"
        description="Contact Premier Global Sourcing by phone, WhatsApp or email to discuss product sourcing, trading and logistics coordination."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title="Talk to our international trade team"
        intro="Reach us through your preferred channel. Share your product, quantity and destination and we will review the requirement and respond with the next steps."
        breadcrumbs={[{ label: "Contact" }]}
      />

      <section className="container-page py-20 lg:py-28">
        <Stagger className="grid gap-6 md:grid-cols-3">
          {channels.map((c) => (
            <StaggerItem key={c.label}>
              <div className="h-full bg-white border border-[#D9E0E4] rounded-lg p-7 flex flex-col" data-testid={`contact-${c.label.toLowerCase()}`}>
                <div className="h-11 w-11 rounded-md bg-[#EAF1F4] flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-teal" />
                </div>
                <p className="mt-5 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</p>
                <p className="mt-1 font-heading font-bold text-[18px] text-navy break-words">{c.value}</p>
                <div className="mt-5 pt-5 border-t border-[#D9E0E4]">
                  <CTAButton
                    href={c.href}
                    variant="outline"
                    className="w-full min-h-[44px]"
                    {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {c.cta}
                  </CTAButton>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-8 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="h-full bg-navy text-white rounded-lg p-8">
              <h2 className="font-heading font-bold text-[22px]">Our offices</h2>
              <ul className="mt-6 space-y-5 text-[15px]">
                {(contact.locations || []).map((loc) => (
                  <li key={loc} className="flex gap-3"><MapPin className="h-5 w-5 text-copper shrink-0 mt-0.5" /><span className="text-white/85">{loc}</span></li>
                ))}
                <li className="flex gap-3"><Clock className="h-5 w-5 text-copper shrink-0 mt-0.5" /><span className="text-white/85">{contact.openingHours}</span></li>
                <li className="flex gap-3"><Mail className="h-5 w-5 text-copper shrink-0 mt-0.5" /><a href={`mailto:${contact.email}`} className="text-white/85 hover:text-white">{contact.email}</a></li>
              </ul>
              <p className="mt-8 text-[13px] text-white/50">{company.legalName}</p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="h-full min-h-[320px] rounded-lg border border-dashed border-[#C3CDD3] bg-white flex flex-col items-center justify-center text-center p-10" data-testid="map-placeholder">
              {contact.mapsEmbed ? (
                <iframe
                  title="Office location map"
                  src={contact.mapsEmbed}
                  className="w-full h-full min-h-[320px] rounded-lg border-0"
                  loading="lazy"
                />
              ) : (
                <>
                  <div className="h-12 w-12 rounded-full bg-[#EAF1F4] flex items-center justify-center">
                    <MapPinned className="h-6 w-6 text-teal" />
                  </div>
                  <p className="mt-5 text-[15px] font-medium text-navy">Map embed placeholder</p>
                  <p className="mt-1.5 text-[13.5px] text-muted-foreground max-w-sm">
                    A verified Google Maps embed will appear here once the map link is added to the site configuration.
                  </p>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
